"use strict";
/**
 * Generate a code challenge for the Proof Key for Code Exchange
 * ([PKCE](https://oauth.net/2/pkce/)) extension to OAuth's Authorization Code
 * flow using the standard Web crypto APIs:
 * - https://developer.mozilla.org/en-US/docs/Web/API/Crypto
 * - https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const base64 = __importStar(require("base64-js"));
const NODE_ENV = process.env.NODE_ENV;
const CRYPTO_AVAILABLE = typeof window !== "undefined" &&
    typeof window.crypto !== "undefined" &&
    typeof window.crypto.subtle !== "undefined";
const CHARSET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
// Values for development when Crypto APIs are not available (e.g. when
// accessing the app at a local IP address over http).
const DEV_CODE_VERIFIER = "Fb0CwDsg8UhuZ59R90uzUILg8Bvf2Ru7SdfCgtlXJzoMJRYbawAbq1DAX83CG28l";
const DEV_CODE_CHALLENGE = "BpUcsk3a7qhcXy-X7VcpPAxJFOKONPU6G3wGBs1mn4A";
/**
 * Generate a code verifier according to the PKCE spec:
 * https://datatracker.ietf.org/doc/html/rfc7636#section-4.1
 *
 * NOTE: If NODE_ENV is "development" and crypto is not available a hardcoded
 * development value will be returned. This value may not match the requested
 * length.
 */
const generateCodeVerifier = (length) => {
    const buffer = new Uint8Array(length);
    const code = [];
    if (length < 43 || length > 128) {
        throw new Error("Invalid code length.");
    }
    else if (!CRYPTO_AVAILABLE && NODE_ENV === "development") {
        return DEV_CODE_VERIFIER;
    }
    else if (!CRYPTO_AVAILABLE) {
        throw new Error("The window.crypto API is not available.");
    }
    window.crypto.getRandomValues(buffer);
    buffer.forEach((value) => {
        const index = value % CHARSET.length;
        code.push(CHARSET[index]);
    });
    return code.join("");
};
/**
 * Derive a code challenge from a code verifier according to the PKCE spec:
 * https://datatracker.ietf.org/doc/html/rfc7636#section-4.2
 *
 * NOTE: If NODE_ENV is "development", crypto is not available, and the
 * hardcoded DEV_CODE_VERIFIER value is passed in then a hardcoded challenge
 * will be returned.
 */
const deriveCodeChallenge = (codeVerifier) => {
    if (codeVerifier.length < 43 || codeVerifier.length > 128) {
        return Promise.reject(new Error("Invalid code length."));
    }
    else if (!CRYPTO_AVAILABLE &&
        NODE_ENV === "development" &&
        codeVerifier === DEV_CODE_VERIFIER) {
        return Promise.resolve(DEV_CODE_CHALLENGE);
    }
    else if (!CRYPTO_AVAILABLE) {
        return Promise.reject("The window.crypto.subtle API is not available.");
    }
    return new Promise((resolve, reject) => {
        crypto.subtle
            .digest("SHA-256", new TextEncoder().encode(codeVerifier))
            .then((buffer) => {
            const encoded = base64.fromByteArray(new Uint8Array(buffer));
            resolve(encoded.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, ""));
        })
            .catch(reject);
    });
};
const generatePkceChallenge = async () => {
    const codeVerifier = generateCodeVerifier(64);
    const codeChallenge = await deriveCodeChallenge(codeVerifier);
    return { codeVerifier, codeChallenge };
};
exports.default = generatePkceChallenge;
